/**
 * 인포트렌드 사이징 툴 — 화면 부품 (v2.233.0 · v2.233.3 PC 다시 보기)
 *
 * 엑셀 원본의 「초록 칸에 넣고 노란 칸에서 본다」 모양을 그대로 따른다: 입력 상자는 초록 테두리, 결과 상자는 노란 테두리.
 * 숫자 칸(NumInput)은 글자를 그대로 들고 있다가 숫자로 읽히면 바로 부모에 알린다 — 지우고 다시 적을 때 0 이 끼어들지 않게.
 *
 * v2.233.3 (10-09 · 사용자 「PC 에서 쓰기에 글씨가 작고 여백이 많고 가운데로 쏠려 있다」):
 *   글자를 한 단계씩 키웠다(라벨·표 12px → 14px · 입력칸 14px → 16px · 결과 숫자 18px → 24px) ·
 *   결과(Stat)는 세로 줄 목록 대신 **가로로 펼쳐지는 타일**(StatGrid · 상자 폭에 맞춰 한 줄에 2~4개) —
 *   넓은 PC 화면에서 「이름은 왼쪽 끝 · 숫자는 오른쪽 끝」으로 1m 떨어지던 것을 없앤다.
 */
import { useRef, useState } from 'react';
import type { ReactNode } from 'react';

export const fmt = (n: number | null | undefined, digits = 0): string =>
  n === null || n === undefined || !Number.isFinite(n) ? '—' : n.toLocaleString('ko-KR', { minimumFractionDigits: digits, maximumFractionDigits: digits });

export function Panel({ title, tone = 'plain', children, note, className = '' }: { title: string; tone?: 'input' | 'result' | 'plain'; children: ReactNode; note?: ReactNode; className?: string }) {
  const cls = tone === 'input' ? 'border-emerald-300 bg-emerald-50/40' : tone === 'result' ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200 bg-white';
  return (
    <section className={`rounded border ${cls} p-4 md:p-5 min-w-0 ${className}`}>
      <h3 className="ui-subsection-title mb-3 flex items-center gap-2">
        {tone === 'input' && <span className="inline-block w-3 h-3 rounded-sm bg-emerald-400 shrink-0" aria-hidden />}
        {tone === 'result' && <span className="inline-block w-3 h-3 rounded-sm bg-amber-400 shrink-0" aria-hidden />}
        {title}
      </h3>
      {children}
      {note && <p className="text-xs text-slate-500 mt-3 leading-relaxed">{note}</p>}
    </section>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    // 폰(768px 미만)에서는 이름 아래에 칸을 둔다 — 두 칸으로 두면 긴 드롭다운(호스트 보드)이 이름을 세로로 찌그러뜨린다 (10-08 폰 실측)
    <label className="grid grid-cols-1 md:grid-cols-[minmax(0,13rem)_minmax(0,1fr)] md:items-center gap-x-3 gap-y-1 py-1.5">
      <span className="text-sm text-slate-700 leading-snug">
        {label}
        {hint && <span className="block text-xs text-slate-500 font-normal">{hint}</span>}
      </span>
      <span className="min-w-0">{children}</span>
    </label>
  );
}

export function NumInput({
  value, onChange, min, max, step, suffix, width = 'w-32',
}: { value: number; onChange: (n: number) => void; min?: number; max?: number; step?: number; suffix?: string; width?: string }) {
  const [text, setText] = useState(String(value));
  const last = useRef(value);
  if (last.current !== value) {
    // 부모가 값을 바꿨을 때(초기화 등)만 글자를 맞춘다 — 사람이 적는 중에는 건드리지 않는다
    last.current = value;
    if (Number(text) !== value) setText(String(value));
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <input
        type="number"
        inputMode="decimal"
        value={text}
        min={min}
        max={max}
        step={step ?? 'any'}
        onChange={(e) => {
          setText(e.target.value);
          const n = Number(e.target.value);
          // 범위 밖(음수 · 0 등)은 적는 동안엔 넘기지 않는다 — 음수 디스크 수 같은 엉뚱한 계산이 잠깐이라도 안 보이게 (10-08 검토)
          if (e.target.value.trim() === '' || !Number.isFinite(n)) return;
          if ((min !== undefined && n < min) || (max !== undefined && n > max)) return;
          onChange(n);
        }}
        onBlur={() => {
          const n = Number(text);
          if (text.trim() === '' || !Number.isFinite(n)) { setText(String(value)); return; }
          // 칸을 벗어날 때 범위로 맞춰 준다 (최소·최대 밖이면 그 값으로)
          const clamped = Math.min(max ?? Infinity, Math.max(min ?? -Infinity, n));
          if (clamped !== n) { setText(String(clamped)); onChange(clamped); }
        }}
        className={`${width} text-base text-right tabular-nums border border-slate-300 rounded px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400`}
      />
      {suffix && <span className="text-sm text-slate-500 whitespace-nowrap">{suffix}</span>}
    </span>
  );
}

export function SelectInput<T extends string | number>({
  value, options, onChange, width = 'w-full md:w-auto',
}: { value: T; options: readonly { value: T; label: string }[]; onChange: (v: T) => void; width?: string }) {
  const isNum = typeof value === 'number';
  return (
    <select
      value={String(value)}
      onChange={(e) => onChange((isNum ? Number(e.target.value) : e.target.value) as T)}
      className={`ui-input ${width} max-w-full`}
    >
      {options.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
    </select>
  );
}

/**
 * 결과 타일 묶음 — 상자 폭에 맞춰 한 줄에 들어가는 만큼(타일 하나 ≥ 13rem) 펼친다.
 * 폰에서는 한 줄에 1~2개, PC 결과 상자(≈1,000px)에서는 4개.
 */
export function StatGrid({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`grid grid-cols-[repeat(auto-fit,minmax(13rem,1fr))] gap-2 ${className}`}>{children}</div>;
}

/**
 * 입력 | 결과 두 상자 나란히 — PC(lg 이상)에서 결과 쪽(표·타일)을 조금 더 넓게(5:7) · 넓은 PC(1700px 이상)는 1:2
 * (입력 상자는 이름 13rem + 칸이면 충분해서 더 넓어 봐야 빈 자리만 는다 · 10-09 1920 실측) · 그 아래(폰·태블릿)에서는 세로로.
 */
export const PAIR_GRID = 'grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] min-[1700px]:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-4';
/** 입력 둘 + 결과 하나 — 넓은 PC(1800px 이상)에서만 셋 나란히 · lg 는 입력 둘 나란히 + 결과는 아래 한 줄(결과 상자 안 표가 좁으면 잘린다 · 10-09 1536 실측) */
export const TRIO_GRID = 'grid grid-cols-1 lg:grid-cols-2 min-[1800px]:grid-cols-3 gap-4';
export const TRIO_RESULT = 'lg:col-span-2 min-[1800px]:col-span-1';

export function Stat({ label, value, unit, sub, big, tone }: { label: ReactNode; value: ReactNode; unit?: string; sub?: ReactNode; big?: boolean; tone?: 'warn' | 'ok' }) {
  const color = tone === 'warn' ? 'text-red-700' : tone === 'ok' ? 'text-emerald-700' : 'text-slate-800';
  // 큰 값(big)은 테두리·바탕을 한 톤 진하게 — 한눈에 「이게 답」이라고 보이게
  const box = big ? 'border-amber-300 bg-amber-100/60' : 'border-amber-200/80 bg-white/70';
  return (
    <div className={`rounded border ${box} px-3 py-2 min-w-0 flex flex-col`}>
      <div className="text-xs text-slate-600 leading-snug">{label}</div>
      <div className={`${big ? 'text-2xl' : 'text-lg'} font-semibold tabular-nums ${color} leading-tight mt-auto pt-1 break-words`}>
        {value}
        {unit && <span className="text-sm font-normal text-slate-500 ml-1 whitespace-nowrap">{unit}</span>}
      </div>
      {sub && <div className="text-xs text-slate-500 mt-0.5 leading-snug">{sub}</div>}
    </div>
  );
}

/**
 * 표 — 숫자 표는 칸을 한 줄로(whitespace-nowrap · 좁으면 옆으로 민다) · 글이 든 표(안내 탭 · 모델 이름 머리글)는
 * `wrap` 으로 줄바꿈을 허용해 폭에 맞춘다(안 그러면 PC 에서도 오른쪽 칸이 잘린다 · 10-09 실측).
 * 폰에서는 글자·여백을 한 단계 작게(12px) — 10-08 폰 실측 때 모양을 지킨다.
 * `textClass` = 글자 크기만 바꿀 때(안내 탭은 PC 13px · v2.313.2).
 */
export function SimpleTable({ head, rows, align = [], wrap = false, textClass = 'text-xs md:text-sm' }: { head: readonly ReactNode[]; rows: readonly (readonly ReactNode[])[]; align?: readonly ('l' | 'r' | 'c')[]; wrap?: boolean; textClass?: string }) {
  const cls = (i: number): string => (align[i] === 'r' ? 'text-right tabular-nums' : align[i] === 'c' ? 'text-center' : 'text-left');
  const ws = wrap ? 'whitespace-normal align-top' : 'whitespace-nowrap';
  return (
    <div className="overflow-x-auto -mx-1">
      <table className={`min-w-full ${textClass} border-collapse`}>
        <thead>
          <tr className="bg-slate-100 text-slate-700">
            {head.map((h, i) => <th key={i} className={`px-2 py-1.5 md:px-3 md:py-2 font-semibold ${wrap ? 'whitespace-normal align-bottom' : 'whitespace-nowrap'} border-b border-slate-200 ${cls(i)}`}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="odd:bg-white even:bg-slate-50/60">
              {r.map((c, ci) => <td key={ci} className={`px-2 py-1.5 md:px-3 md:py-2 ${ws} border-b border-slate-100 ${cls(ci)}`}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 「계산 방식」 접이식 설명 — 엑셀 수식을 한국어로 풀어 적는다 */
export function Formula({ title = '계산 방식', children }: { title?: string; children: ReactNode }) {
  return (
    <details className="mt-3 text-xs text-slate-600">
      <summary className="cursor-pointer select-none text-sm text-slate-500 hover:text-slate-700">📐 {title}</summary>
      <div className="mt-1.5 pl-3 border-l-2 border-slate-200 space-y-1 leading-relaxed">{children}</div>
    </details>
  );
}

export function Dash() {
  return <span className="text-slate-400">—</span>;
}
